import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import subscriptionApi from "../api/subscriptionApi";
import { useAuth } from "../context/AuthContext";
import Btn from "../components/Btn";
import T from "../theme/colors";

/**
 * Phase 36da.99.gj — landing page for the PhonePe redirect
 * (/subscription/callback?orderId=SUB...).  Calls /verify (which checks the
 * PhonePe Status API server-side), then lets the user continue into the app.
 */
export default function SubscriptionCallbackPage() {
    const { refreshSubscription } = useAuth();
    const [state, setState] = useState("checking"); // checking | success | pending | failed

    useEffect(() => {
        const orderId = new URLSearchParams(window.location.search).get("orderId");
        if (!orderId) { setState("failed"); return; }
        let tries = 0;
        const run = async () => {
            try {
                const res = await subscriptionApi.verify(orderId);
                const data = res?.data?.data ?? res?.data;
                const code = data?.phonepeCode;
                if (data?.paid || code === "PAYMENT_SUCCESS") {
                    await refreshSubscription();
                    setState("success");
                } else if (code === "PAYMENT_PENDING" || !code) {
                    // Give PhonePe a moment, retry a few times before giving up.
                    if (tries++ < 4) { setTimeout(run, 2500); }
                    else setState("pending");
                } else {
                    setState("failed");
                }
            } catch (_) {
                if (tries++ < 4) { setTimeout(run, 2500); }
                else setState("pending");
            }
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goHome = () => { window.location.href = "/"; };

    const view = {
        checking: {
            icon: <Loader2 size={34} style={{ animation: "spin 0.8s linear infinite" }} />, color: "var(--primary)", bg: "var(--bg-app)",
            title: "Confirming your payment…", msg: "Please wait, this only takes a moment."
        },
        success: {
            icon: <CheckCircle2 size={34} />, color: "var(--success)", bg: "var(--success-bg)",
            title: "Payment successful", msg: "Your subscription has been renewed. Thank you!"
        },
        pending: {
            icon: <Loader2 size={34} style={{ animation: "spin 0.8s linear infinite" }} />, color: "var(--warn)", bg: "var(--warn-bg)",
            title: "Payment is processing", msg: "We haven't received final confirmation yet. It will update automatically once PhonePe confirms."
        },
        failed: {
            icon: <XCircle size={34} />, color: "var(--danger)", bg: "var(--danger-bg)",
            title: "Payment not completed", msg: "The payment didn't go through. You can try again from the app."
        },
    }[state];

    return (
        <div style={{
            minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
            background: "var(--bg-app)", padding: 24, fontFamily: "var(--font-ui)",
        }}>
            <div style={{
                width: "100%", maxWidth: 420, background: "var(--bg-card)", borderRadius: 16,
                border: `1px solid var(--border)`, boxShadow: "var(--shadow-xl)", padding: "38px 34px", textAlign: "center",
            }}>
                <div style={{
                    width: 64, height: 64, borderRadius: "50%", margin: "0 auto 18px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: view.bg, color: view.color,
                }}>
                    {view.icon}
                </div>
                <h1 style={{ margin: "0 0 8px", fontSize: 24, fontWeight: 700, color: "var(--text-0)" }}>{view.title}</h1>
                <p style={{ margin: "0 0 24px", fontSize: 17, lineHeight: 1.55, color: "var(--text-1)" }}>{view.msg}</p>
                {state !== "checking" && (
                    <Btn variant="primary" size="lg" onClick={goHome} style={{ width: "100%", padding: "12px 0" }}>
                        Continue
                    </Btn>
                )}
            </div>
        </div>
    );
}